import React from 'react';
import { Box, Container, Heading, Text, VStack, Button, Badge, HStack, SimpleGrid, List, ListItem, Icon } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaPlay, FaPhone, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';

const MotionBox = motion(Box);

const AIMasteryCTA = ({ data, onEnrollClick }) => {
  return (
    <Box
      py={{ base: 16, md: 20 }}
      bgGradient="linear(to-br, brand.800, brand.900, purple.800)"
      position="relative"
      overflow="hidden"
    >
      {/* Blur effects */}
      <Box
        position="absolute"
        top="10%"
        right="5%"
        w="300px"
        h="300px"
        bg="purple.400"
        opacity="0.1"
        borderRadius="full"
        filter="blur(80px)"
      />

      <Container maxW="container.xl">
        <VStack spacing={12}>
          {/* Badge */}
          {data.badge && (
            <Badge
              px={4}
              py={2}
              bg="purple.400"
              color="white"
              fontSize="sm"
              fontWeight="700"
              borderRadius="full"
            >
              {data.badge}
            </Badge>
          )}

          {/* Title */}
          <VStack spacing={4} textAlign="center">
            <Heading
              size="2xl"
              fontWeight="900"
              color="white"
              maxW="4xl"
            >
              {data.title}
            </Heading>
            {data.subtitle && (
              <Text fontSize="xl" fontWeight="700" color="purple.200">
                {data.subtitle}
              </Text>
            )}
          </VStack>

          {/* Developer Types Comparison */}
          {data.developerTypes && (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="full">
              {/* The Scrambler */}
              <MotionBox
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Box p={8} bg="red.50" borderRadius="2xl" h="full">
                  <VStack spacing={4} align="stretch">
                    <Heading size="lg" color="red.600">
                      {data.developerTypes.type1.label}
                    </Heading>
                    <List spacing={3}>
                      {data.developerTypes.type1.traits?.map((trait, i) => (
                        <ListItem key={i}>
                          <HStack align="flex-start" spacing={3}>
                            <Icon as={FaTimesCircle} color="red.500" boxSize={5} mt={1} />
                            <Text color="brand.700">{trait}</Text>
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  </VStack>
                </Box>
              </MotionBox>

              {/* The Master */}
              <MotionBox
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Box p={8} bg="green.50" borderRadius="2xl" h="full">
                  <VStack spacing={4} align="stretch">
                    <Heading size="lg" color="accent.green">
                      {data.developerTypes.type2.label}
                    </Heading>
                    <List spacing={3}>
                      {data.developerTypes.type2.traits?.map((trait, i) => (
                        <ListItem key={i}>
                          <HStack align="flex-start" spacing={3}>
                            <Icon as={FaCheckCircle} color="accent.green" boxSize={5} mt={1} />
                            <Text color="brand.700">{trait}</Text>
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  </VStack>
                </Box>
              </MotionBox>
            </SimpleGrid>
          )}

          {/* Question */}
          {data.question && (
            <Text fontSize="2xl" fontWeight="900" color="accent.gold" textAlign="center">
              {data.question}
            </Text>
          )}

          {/* Urgency Section */}
          {data.urgency && (
            <Box p={8} bg="whiteAlpha.100" borderRadius="2xl" w="full">
              <VStack spacing={4}>
                <Heading size="lg" color="white">
                  {data.urgency.title}
                </Heading>
                <List spacing={3}>
                  {data.urgency.reasons?.map((reason, i) => (
                    <ListItem key={i}>
                      <HStack align="flex-start" spacing={3}>
                        <Icon as={FaCheckCircle} color="accent.gold" boxSize={5} mt={1} />
                        <Text color="whiteAlpha.900">{reason}</Text>
                      </HStack>
                    </ListItem>
                  ))}
                </List>
              </VStack>
            </Box>
          )}

          {/* Description (fallback) */}
          {data.description && !data.developerTypes && (
            <Text
              fontSize="xl"
              color="whiteAlpha.900"
              maxW="3xl"
              textAlign="center"
            >
              {data.description}
            </Text>
          )}

          {/* CTA Buttons */}
          <VStack spacing={4} pt={4}>
            <HStack spacing={4} flexWrap="wrap" justify="center">
              {data.primaryButton && (
                <Button
                  as={RouterLink}
                  to={data.primaryButton.href}
                  onClick={onEnrollClick}
                  size="lg"
                  bg="accent.green"
                  color="white"
                  px={8}
                  py={7}
                  fontSize="lg"
                  fontWeight="700"
                  rightIcon={<FaArrowRight />}
                  _hover={{
                    bg: 'green.500',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(16,185,129,0.4)',
                  }}
                  transition="all 0.3s"
                >
                  {data.primaryButton.text}
                </Button>
              )}

              {data.secondaryButton && (
                <Button
                  as={RouterLink}
                  to={data.secondaryButton.href}
                  size="lg"
                  variant="outline"
                  borderColor="white"
                  color="white"
                  px={8}
                  py={7}
                  fontSize="md"
                  fontWeight="600"
                  leftIcon={<FaPlay />}
                  _hover={{
                    bg: 'whiteAlpha.200',
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.3s"
                >
                  {data.secondaryButton.text}
                </Button>
              )}
            </HStack>

            {data.tertiaryButton && (
              <Button
                as={RouterLink}
                to={data.tertiaryButton.href}
                size="md"
                variant="ghost"
                color="whiteAlpha.900"
                fontWeight="600"
                leftIcon={<FaPhone />}
                _hover={{
                  bg: 'whiteAlpha.100',
                }}
              >
                {data.tertiaryButton.text}
              </Button>
            )}
          </VStack>

          {/* Footer */}
          {data.footer && (
            <Text
              fontSize="sm"
              color="whiteAlpha.800"
              mt={4}
              textAlign="center"
            >
              {data.footer}
            </Text>
          )}

          {/* Final Thought */}
          {data.finalThought && (
            <Text
              fontSize="lg"
              color="whiteAlpha.900"
              fontStyle="italic"
              textAlign="center"
              maxW="3xl"
              pt={4}
            >
              {data.finalThought}
            </Text>
          )}

          {/* Visual Placeholder */}
          {data.visualPlaceholder && (
            <Box
              p={6}
              bg="whiteAlpha.100"
              borderRadius="xl"
              borderStyle="dashed"
              borderWidth="2px"
              borderColor="whiteAlpha.300"
              textAlign="center"
              w="full"
            >
              <Text color="whiteAlpha.800" fontSize="sm" fontStyle="italic">
                {data.visualPlaceholder}
              </Text>
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default AIMasteryCTA;
